import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

/** Mirrors backend OrderStatus enum (catax_ai/dal/models/orders.py) */

export type OrderStatus =
  | 'draft'              // Work in progress — not yet submitted
  | 'submitted'          // Submitted by the user / customer, waiting for processing
  | 'in_progress'        // Being actively processed / worked on
  | 'pending_assistance' // Blocked — waiting for help / clarification
  | 'error'              // Intake/processing failed — details in status notes
  | 'completed'          // Fulfilled / closed successfully
  | 'cancelled';         // Cancelled before completion

export type OrderStage =
  | 'received'
  | 'analysing'
  | 'waiting_for_input'
  | 'awaiting_info'
  | 'results_ready'
  | 'in_cart'
  | 'submitted'
  | 'confirmed'
  | 'completed'
  | 'cancelled';

export type OrderAttentionFlag =
  | 'needs_you'
  | 'needs_customer'
  | 'waiting_customer'
  | 'system_error';

export type OrderOwnerType = 'agent' | 'unassigned' | 'person';

export interface OrderItem {
  id: string;
  structured_data: {
    part_data: {
      part_id: string;
      description: string;
      price: string;
    };
    vin_data: {
      vin: string;
    };
    selection_reason?: string;
  };
  quantity: number;
  image?: string;
  timestamp: Date;
}

export interface OrderCreateRequest {
  name: string;
  transaction_id: string;
  external_order_id: string;
  parts_in_order: {
    items: OrderItem[];
    total_items: number;
    export_timestamp: string;
    vins: string[];
  };
  status?: OrderStatus;
  source?: string;
  vin?: string | null;
  buyer?: string | null;
  session_id?: string | null;
  service_type?: string | null;
  language?: string | null;
  vin_data?: any | null;
  discount_snapshot?: any | null;
  /** Per-order part-selection mode: 'best_effort' | 'human_in_the_loop' */
  part_selection_mode?: 'best_effort' | 'human_in_the_loop' | null;
}

export interface OrderResponse {
  /**
   * Discriminates a real order row ("order") from a chat session that never
   * became an order ("session"). Session rows arrive with id/name/transaction_id/
   * external_order_id null at runtime; the orders table guards on `kind` before
   * touching those, so the shared type keeps them required for all other
   * consumers (which only ever receive real orders).
   */
  kind?: 'order' | 'session';
  id: number;
  uid: string;
  name: string;
  transaction_id: string;
  external_order_id: string;
  parts_in_order: any;
  status: OrderStatus;
  stage?: OrderStage | null;
  attention_flag?: OrderAttentionFlag | null;
  attention_reason?: string | null;
  attention_details?: Record<string, any> | null;
  owner_type?: OrderOwnerType | null;
  owner_uid?: string | null;
  /** Display name of the named owner (org view). */
  owner_name?: string | null;
  stage_changed_at?: string | null;
  attention_changed_at?: string | null;
  ownership_changed_at?: string | null;
  /** Where this order originated: 'user' | 'guest' | 'whatsapp' | 'email' | 'website' | 'api' */
  source?: string | null;
  created_at: string;
  updated_at: string;
  session_id: string;
  vin?: string | null;
  buyer?: string | null;
  service_type?: string | null;
  vin_data?: any | null;
  /**
   * Externally-synced enrichment, merge-written by outside sync workflows via
   * PATCH /orders/{id}/extra-data. Known keys: alt_model — local-market
   * alternative vehicle model name(s).
   */
  extra_data?: Record<string, any> | null;
  catalog_used?: string | null;
  /** Name of the user who created the order (populated for org-level queries) */
  created_by_name?: string | null;
  /** Workflow audit trail: [{ts, note, actor, step_type}] */
  status_notes?: Array<{ ts: string; note: string; actor: string; step_type: string | null }> | null;
  /** Discount/pricing snapshot persisted at submission time */
  discount_snapshot?: any | null;
  /** Linked B2B CRM customer id */
  b2b_customer_id?: number | null;
  /**
   * Denormalised requested-vs-found summary mirrored from the chat session.
   * parts_in_order holds only the cart; this captures the full wishlist.
   */
  parts_summary?: {
    requested?: string[];
    found?: string[];
    not_found?: string[];
    unresolved?: string[];
    counts?: { requested: number; found: number };
  } | null;
  /** Per-order part-selection mode: 'best_effort' | 'human_in_the_loop' (null → default) */
  part_selection_mode?: 'best_effort' | 'human_in_the_loop' | null;
  /** Session rows only: true when the chat session is paused on a clarification
   *  question and is waiting for the user to answer before it can continue. */
  needs_clarification?: boolean;
  /** Session rows only: true when the session captured a VIN but catalog
   *  verification failed — rendered as the session's "status". */
  vin_verification_failed?: boolean;
  /** Session rows only: true while the async API search creating this order
   *  is still running (the order row doesn't exist yet). */
  processing?: boolean;
  /** Labelled, catalog-agnostic vehicle facts (see bl/vehicle_facts). */
  vehicle_facts?: {
    facts: { key: string; label: string; value: string }[];
    enriched_model?: string | null;
    sources: string[];
    catalog?: string | null;
  } | null;
  /** Linked B2B CRM customer details (populated when b2b_customer_id is set) */
  b2b_customer?: {
    id: number;
    name: string;
    alias?: string | null;
    tax_id?: string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    city?: string | null;
    country?: string | null;
  } | null;
}

export interface OrderListResponse {
  status: string;
  orders: OrderResponse[];
  total_count: number;
  /** Live chip counts (per stage group, `all`, `needs_action`) under the active filters. */
  stage_counts?: Record<string, number> | null;
}

/** Server-side order search/filters — applied across ALL records so results and
 *  pagination reflect the whole dataset, not just the loaded page. */
export interface OrderFilters {
  search?: string;   // free text over order id / name / VIN / customer
  make?: string;     // vehicle make/model (vin_data.datasetName)
  vin?: string;
  source?: string;
  service?: string;  // service_type
  customer?: string; // buyer / B2B customer name
  dateFrom?: string; // YYYY-MM-DD (created_at lower bound)
  /** Lifecycle filters (spec §3, §6). */
  stageGroup?: StageGroup | '';
  attention?: OrderAttentionFlag | '';
  owner?: OwnerFilter | '';
  needsAction?: boolean;
}

export type StageGroup = 'intake' | 'review' | 'cart' | 'ordered' | 'cancelled';
export type OwnerFilter = 'mine' | 'unassigned' | 'agent';

export interface LifecycleSummary {
  needs_action: number;
  needs_owner: number;
  by_flag: Record<OrderAttentionFlag, number>;
  week?: { orders: number; matched_pct: number | null; sent: number };
}

export interface OrderNotification {
  id: number;
  order_id: number;
  external_order_id?: string | null;
  kind: OrderAttentionFlag | 'results_ready' | string;
  reason?: string | null;
  title: string;
  body?: string | null;
  delivery: string;
  read_at?: string | null;
  created_at: string;
  queue: boolean;
}

export interface ReplyDraft {
  channel: string;
  to?: string | null;
  subject: string;
  body: string;
  language: string;
  reason: string;
  can_send: boolean;
}

export interface LifecycleQuestions {
  questions: any[];
  message: string;
  resolved_parts: { name?: string; score?: number | null }[];
  requested_parts?: any[];
}

export interface LifecycleLog {
  error_code?: string | null;
  message?: string | null;
  attempts?: number | null;
  max_attempts?: number | null;
  final?: boolean | null;
  next_retry_at?: string | null;
  log: { ts?: string; attempt?: number; message?: string; traceback?: string }[];
  notes: any[];
  stage?: string | null;
}

export interface OrderShareCreateRequest {
  shared_with_email: string;
}

export interface OrderShareResponse {
  id: number;
  order_id: number;
  owner_uid: string;
  shared_with_uid?: string | null;
  shared_with_email: string;
  permission: 'view';
  status: 'accepted' | 'revoked';
  token: string;
  expires_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrgMember {
  uid: string;
  name: string;
  email: string;
  picture?: string | null;
}

export interface OrderShareListResponse {
  status: string;
  shares: OrderShareResponse[];
}

export class OrderService {
  private static instance: OrderService;

  public static getInstance(): OrderService {
    if (!OrderService.instance) {
      OrderService.instance = new OrderService();
    }
    return OrderService.instance;
  }

  // Create a new order
  public async createOrder(orderData: OrderCreateRequest): Promise<OrderResponse> {
    const response = await apiService.post<OrderResponse>('/api/user/orders/', orderData);
    return response.data;
  }

  /** Server-side search/filters applied across ALL records (not just a page). */
  private buildOrderParams(
    status: string | undefined,
    limit: number,
    offset: number,
    includeSessions: boolean,
    filters?: OrderFilters,
  ): URLSearchParams {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (includeSessions) params.append('include_sessions', 'true');
    if (filters) {
      if (filters.search?.trim()) params.append('search', filters.search.trim());
      if (filters.make?.trim()) params.append('make', filters.make.trim());
      if (filters.vin?.trim()) params.append('vin', filters.vin.trim());
      if (filters.source?.trim()) params.append('source', filters.source.trim());
      if (filters.service?.trim()) params.append('service', filters.service.trim());
      if (filters.customer?.trim()) params.append('customer', filters.customer.trim());
      if (filters.dateFrom?.trim()) params.append('date_from', filters.dateFrom.trim());
      if (filters.stageGroup) params.append('stage_group', filters.stageGroup);
      if (filters.attention) params.append('attention', filters.attention);
      if (filters.owner) params.append('owner', filters.owner);
      if (filters.needsAction) params.append('needs_action', 'true');
    }
    return params;
  }

  // Get all user orders
  public async getUserOrders(
    status?: string,
    limit: number = 50,
    offset: number = 0,
    includeSessions: boolean = false,
    filters?: OrderFilters,
  ): Promise<OrderListResponse> {
    const params = this.buildOrderParams(status, limit, offset, includeSessions, filters);
    const response = await apiService.get<OrderListResponse>(
      `/api/user/orders/?${params.toString()}`
    );
    return response.data;
  }

  // Get order by ID
  public async getOrderById(orderId: number): Promise<OrderResponse> {
    const response = await apiService.get<OrderResponse>(`/api/user/orders/${orderId}`);
    return response.data;
  }

  // State of the order's in-flight cart pricing runs in Temporal
  public async getPricingStatus(orderId: number): Promise<{ state: 'idle' | 'running' | 'queued'; queued_seconds: number }> {
    const response = await apiService.get<{ state: 'idle' | 'running' | 'queued'; queued_seconds: number }>(`/api/user/orders/${orderId}/pricing-status`);
    return response.data;
  }

  // List the source files of an insurance case linked to this order (signed URLs)
  public async getCaseFiles(orderId: number): Promise<{
    insurer?: string | null;
    case_id?: string | null;
    vin?: string | null;
    parts?: string[];
    description?: string | null;
    summary?: Array<{ label: string; value: string }>;
    files: Array<{ name: string; mime: string; url: string; gs_uri: string }>;
  }> {
    const response = await apiService.get(`/api/user/orders/${orderId}/case-files`);
    return response.data;
  }

  // Get order by external ID
  public async getOrderByExternalId(externalOrderId: string): Promise<OrderResponse> {
    const response = await apiService.get<OrderResponse>(
      `/api/user/orders/external/${externalOrderId}`
    );
    return response.data;
  }

  // Update order
  public async updateOrder(orderId: number, updateData: Partial<OrderCreateRequest>): Promise<OrderResponse> {
    const response = await apiService.put<OrderResponse>(`/api/user/orders/${orderId}`, updateData);
    return response.data;
  }

  /** Re-run the add-to-cart pricing/inventory workflow for a single existing line.
   *  Clears the line's cached price/availability server-side and re-fires the
   *  `item_added_to_cart` event, returning the (possibly already-refreshed) order. */
  public async recheckCartItem(orderId: number, itemId: string): Promise<OrderResponse> {
    const response = await apiService.post<OrderResponse>(
      `/api/user/orders/${orderId}/recheck-item`,
      null,
      { params: { item_id: itemId } }
    );
    return response.data;
  }

  // Update order status
  public async updateOrderStatus(orderId: number, status: string): Promise<{ message: string; order_id: number; new_status: string }> {
    const formData = new FormData();
    formData.append('status', status);

    const response = await apiService.patch(`/api/user/orders/${orderId}/status?status=${encodeURIComponent(status)}`);
    return response.data;
  }

  // Delete order
  public async deleteOrder(orderId: number): Promise<{ message: string; order_id: number }> {
    const response = await apiService.delete(`/api/user/orders/${orderId}`);
    return response.data;
  }

  // Reset chat session for an order
  public async resetOrderChat(orderId: number, language: string): Promise<{ message: string; order_id: number; new_session_id: string; previous_session_archived: boolean }> {
    const response = await apiService.post(`/api/user/orders/${orderId}/reset-chat/${language}`);
    return response.data;
  }

  // Remove part from order
  public async removePartFromOrder(orderId: number, partId: string): Promise<{ message: string; order_id: number; part_id: string }> {
    const response = await apiService.delete(`/api/user/orders/${orderId}/parts/${partId}`);
    return response.data;
  }

  // Helper method to generate transaction ID
  public generateTransactionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `TXN-${timestamp}-${random}`;
  }

  // Helper method to generate external order ID
  public generateExternalOrderId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `EXT-${timestamp}-${random}`;
  }
  // Identify VIN
  public async identifyVin(vin: string): Promise<AxiosResponse<any>> {
    return apiService.post(`/api/user/orders/identify-vin?vin=${encodeURIComponent(vin)}`);
  }

  // Identify VIN from file
  public async identifyVinFile(file: File): Promise<AxiosResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    return apiService.post('/api/user/orders/identify-vin-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Share an order with another user
  public async shareOrder(orderId: number, data: OrderShareCreateRequest): Promise<OrderShareResponse> {
    const response = await apiService.post<OrderShareResponse>(`/api/user/orders/${orderId}/share`, data);
    return response.data;
  }

  // Get all shares for an order
  public async getOrderShares(orderId: number): Promise<OrderShareListResponse> {
    const response = await apiService.get<OrderShareListResponse>(`/api/user/orders/${orderId}/shares`);
    return response.data;
  }

  // Revoke a share
  public async revokeShare(orderId: number, shareId: number): Promise<{ message: string; share_id: number }> {
    const response = await apiService.delete(`/api/user/orders/${orderId}/shares/${shareId}`);
    return response.data;
  }

  // Get orders shared with me
  public async getOrdersSharedWithMe(): Promise<OrderListResponse> {
    const response = await apiService.get<OrderListResponse>('/api/user/orders/shared-with-me');
    return response.data;
  }

  // Get all organization orders (user must be part of an org)
  public async getOrgOrders(
    status?: string,
    limit: number = 50,
    offset: number = 0,
    includeSessions: boolean = false,
    filters?: OrderFilters,
  ): Promise<OrderListResponse> {
    const params = this.buildOrderParams(status, limit, offset, includeSessions, filters);
    const response = await apiService.get<OrderListResponse>(
      `/api/user/orders/org?${params.toString()}`
    );
    return response.data;
  }

  // Get all org members (for share picker)
  public async getOrgMembers(): Promise<OrgMember[]> {
    const response = await apiService.get<OrgMember[]>('/api/user/orders/org-members');
    return response.data;
  }

  // Append a workflow audit note to the order's status_notes trail
  public async addWorkflowNote(
    orderId: number,
    note: string,
    stepType: 'edit_price' | 'edit_discount' | 'edit_description' | 'add_order_note' = 'add_order_note'
  ): Promise<{ ok: boolean; note_count: number }> {
    const response = await apiService.post<{ ok: boolean; note_count: number }>(
      `/api/user/orders/${orderId}/notes`,
      { note, step_type: stepType }
    );
    return response.data;
  }

  // Take ownership of an order created by another org member
  public async takeOwnership(orderId: number): Promise<OrderResponse> {
    const response = await apiService.post<OrderResponse>(`/api/user/orders/${orderId}/take-ownership`);
    return response.data;
  }

  // ── Lifecycle (stage / attention / ownership) ─────────────────────────
  private async lifecycleAction(orderId: number, action: string, body?: any): Promise<OrderResponse> {
    const response = await apiService.post<OrderResponse>(`/api/user/orders/${orderId}/lifecycle/${action}`, body ?? {});
    return response.data;
  }
  /** Always returns a well-formed summary — an old backend or an error body never
   *  reaches the UI as a partial object. */
  public getLifecycleSummary = async (): Promise<LifecycleSummary> => {
    const raw: any = (await apiService.get<LifecycleSummary>('/api/user/orders/lifecycle/summary')).data;
    const flags = (raw && typeof raw === 'object' && raw.by_flag) || {};
    return {
      needs_action: Number(raw?.needs_action) || 0,
      needs_owner: Number(raw?.needs_owner) || 0,
      by_flag: {
        needs_you: Number(flags.needs_you) || 0,
        needs_customer: Number(flags.needs_customer) || 0,
        waiting_customer: Number(flags.waiting_customer) || 0,
        system_error: Number(flags.system_error) || 0,
      },
      week: raw?.week && typeof raw.week === 'object' ? raw.week : undefined,
    };
  };
  /** The home hero's LLM-written sentence. Returns null on any failure — the
   *  caller keeps its static greeting rather than showing an empty headline. */
  public getHomeHeadline = async (language: string): Promise<string | null> => {
    try {
      const raw: any = (await apiService.get<{ headline?: string }>(
        `/api/user/orders/lifecycle/headline?language=${encodeURIComponent(language || 'en')}`,
      )).data;
      const headline = String(raw?.headline ?? '').trim();
      return headline || null;
    } catch {
      return null;
    }
  };

  public lifecycleTake = (id: number) => this.lifecycleAction(id, 'take');
  public lifecycleRelease = (id: number) => this.lifecycleAction(id, 'release');
  public lifecycleAssign = (id: number, uid: string | null) => this.lifecycleAction(id, 'assign', { uid });
  public lifecycleRetry = (id: number) => this.lifecycleAction(id, 'retry');
  public lifecycleCancel = (id: number, reason: string, supplierCancelled = false) =>
    this.lifecycleAction(id, 'cancel', { reason, supplier_cancelled: supplierCancelled });
  public lifecycleReopen = (id: number) => this.lifecycleAction(id, 'reopen');
  public lifecycleReply = (id: number, body: string, subject?: string) =>
    this.lifecycleAction(id, 'reply', { body, subject });
  public lifecycleResolveInternally = (id: number) => this.lifecycleAction(id, 'resolve-internally');
  public lifecycleAskCustomer = (id: number) => this.lifecycleAction(id, 'ask-customer');
  public lifecycleMarkAnswered = (id: number) => this.lifecycleAction(id, 'mark-answered');
  /** Close a question that no longer needs an answer — also clears it in the chat,
   *  so reopening the order does not raise it again. */
  public lifecycleDismissQuestion = (id: number) => this.lifecycleAction(id, 'dismiss-question');
  public getReplyDraft = async (id: number): Promise<ReplyDraft> =>
    (await apiService.get<ReplyDraft>(`/api/user/orders/${id}/lifecycle/reply-draft`)).data;
  public getLifecycleQuestions = async (id: number): Promise<LifecycleQuestions> =>
    (await apiService.get<LifecycleQuestions>(`/api/user/orders/${id}/lifecycle/questions`)).data;
  public answerLifecycleQuestions = async (id: number, answers: any[], message: string, language?: string) =>
    (await apiService.post(`/api/user/orders/${id}/lifecycle/answer`, { answers, message, language })).data;
  public getLifecycleLog = async (id: number): Promise<LifecycleLog> =>
    (await apiService.get<LifecycleLog>(`/api/user/orders/${id}/lifecycle/log`)).data;
  public getNotifications = async (unreadOnly = true): Promise<{ unread: number; items: OrderNotification[] }> =>
    (await apiService.get<{ unread: number; items: OrderNotification[] }>(
      `/api/user/orders/lifecycle/notifications?unread_only=${unreadOnly ? 'true' : 'false'}`)).data;
  public markNotificationsRead = async (ids?: number[]) =>
    (await apiService.post('/api/user/orders/lifecycle/notifications/read', { ids: ids ?? null })).data;
}

export const orderService = OrderService.getInstance();
